import { z } from 'zod';
import { isoDate, paginacao, taxa } from './primitives.js';

/**
 * Origem do lucro desejado/minimo que a API entregou ao motor (DP6): `TABELA` (vigencia da tabela define),
 * `EMPRESA` (tabela nula, vale o padrao da empresa) ou `AUSENTE` (nenhum dos dois: minimo 0).
 */
export type OrigemLucro = 'TABELA' | 'EMPRESA' | 'AUSENTE';
export const ROTULO_ORIGEM_LUCRO: Record<OrigemLucro, string> = {
  TABELA: 'da tabela',
  EMPRESA: 'padrão da empresa',
  AUSENTE: 'sem padrão (mínimo 0)',
};

/** Lucro resolvido gravado no snapshot (`refs.lucro`). */
export interface LucroResolvido {
  desejado: { valor: string | null; origem: OrigemLucro; parametroId?: string | null };
  minimo: { valor: string; origem: OrigemLucro; parametroId?: string | null };
}

export const despesaKind = z.enum(['TRIBUTO', 'COMISSAO', 'OUTRA']);
/** Sessao 15 (6b, P2): a linha de despesa e este tributo (a taxa cadastrada e a da legislacao; o motor aplica o fator do ano). `OUTRO` = tributo que nao transita (IRPJ, CSLL, DAS): revisado, sem fator. */
export const despesaTaxCode = z.enum(['ICMS', 'ISS', 'PIS', 'COFINS', 'OUTRO']);
/** P7: base da despesa: preco sem tributos (padrao) ou total da NF. */
export const despesaBase = z.enum(['PRECO_SEM_TRIBUTOS', 'TOTAL_NF']);

// ---- Sessao 50: perfil tributario tipado da vigencia (modo ESTRUTURADO) ----
export const modoTributario = z.enum(['LEGADO', 'ESTRUTURADO']);
export const regimeApuracao = z.enum(['HERDAR_DA_EMPRESA', 'SIMPLES', 'SIMPLES_HIBRIDO', 'PRESUMIDO', 'REAL']);
/** ANTES_IR: a meta de lucro e antes do IRPJ/CSLL; LIQUIDA_IR: e depois (no REAL o divisor usa meta / (1 - aliqLucroReal)). */
export const metaLucro = z.enum(['ANTES_IR', 'LIQUIDA_IR']);
export const incidenciaTributo = z.enum(['DENTRO', 'FORA', 'LUCRO']);
export const TRIBUTOS_PERFIL = ['ICMS', 'FCP', 'DIFAL', 'ICMS_ST', 'PIS', 'COFINS', 'IPI', 'ISS', 'CBS', 'IBS_UF', 'IBS_MUN', 'IS', 'IRPJ', 'CSLL', 'DAS'] as const;
/** Tributos com alíquota própria na tabela. CBS, IBS e IS NÃO entram aqui: vêm só do contexto da reforma (somente leitura na tela). */
export const TRIBUTOS_EDITAVEIS = ['ICMS', 'FCP', 'DIFAL', 'ICMS_ST', 'PIS', 'COFINS', 'IPI', 'ISS', 'IRPJ', 'CSLL', 'DAS'] as const;
export const tributoPerfil = z.enum(TRIBUTOS_EDITAVEIS);
export type TributoPerfil = z.infer<typeof tributoPerfil>;
export const UFS = ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'] as const;
export const uf = z.enum(UFS);

/** Alíquota de lei com ate 6 casas (Decimal(8,6) no banco). */
const aliquota6 = z.string().regex(/^(?:0|[1-9]\d*)(?:\.\d{1,6})?$/, 'Taxa invalida (fracao, ate 6 casas, sem sinal, use ponto)');

/** MVA original da ICMS-ST (fracao, ate 6 casas): 0 <= MVA < 10 (1000%). */
export const mva6 = aliquota6.regex(/^\d(\.\d+)?$/, 'MVA deve ser menor que 1000% (fracao menor que 10)');

/**
 * Sessao 52: Simples calculado. DIGITADA (padrao da API) = dasEfetiva do perfil, como sempre; CALCULADA = efetiva pelos Anexos da LC 123
 * e pelo RBT12/folha da empresa (`/empresa/simples-apuracao`). Anexo por item: SERVICO usa `anexoServico`; os demais tipos, `anexoBem`.
 */
export const modoEfetivaSimples = z.enum(['CALCULADA', 'DIGITADA']);
export const anexoBemSimples = z.enum(['I', 'II']);
export const anexoServicoSimples = z.enum(['III', 'V', 'FATOR_R']);
export const simplesConfigSchema = z.object({
  modoEfetiva: modoEfetivaSimples.default('DIGITADA'),
  anexoBem: anexoBemSimples.default('I'),
  anexoServico: anexoServicoSimples.default('FATOR_R'),
});
export type SimplesConfigDto = z.infer<typeof simplesConfigSchema>;

export const perfilTributarioSchema = z
  .object({
    regimeApuracao,
    metaLucro: metaLucro.default('ANTES_IR'),
    /** Nulo = a UF da empresa. */
    ufOrigem: uf.nullish(),
    aliqLucroReal: aliquota6.default('0.34'),
    presuncaoIrpjBens: aliquota6.default('0.08'),
    presuncaoIrpjServicos: aliquota6.default('0.32'),
    presuncaoCsllBens: aliquota6.default('0.12'),
    presuncaoCsllServicos: aliquota6.default('0.32'),
    aliqIrpj: aliquota6.default('0.15'),
    aliqCsll: aliquota6.default('0.09'),
    adicionalIrpj: z.boolean().default(false),
    acrescimoLc224: z.boolean().default(false),
    dasEfetiva: aliquota6.nullish(),
    /** Simples hibrido: DAS sem CBS/IBS (estes saem por fora). */
    dasEfetivaSemCbsIbs: aliquota6.nullish(),
    /** Sessao 51: a linha ICMS da tabela vale para todas as UFs (sem DIFAL nem interestadual). Padrao desligado: a linha e o ICMS interno. */
    icmsFixo: z.boolean().default(false),
    /** Sessao 51: derivacao do preco nas outras UFs (liberacao por UF). */
    derivacaoUf: z.enum(['LIQUIDO_DENTRO', 'MESMO_LUCRO']).default('LIQUIDO_DENTRO'),
    /** Sessao 52: DIFAL com base dupla (nao contribuinte em outra UF). Padrao desligado = base unica (nada muda). Vigencia imutavel: so em nova vigencia. */
    difalBaseDupla: z.boolean().default(false),
    /** Sessao 52: Simples calculado (so vale nos regimes SIMPLES e SIMPLES_HIBRIDO, ou HERDAR_DA_EMPRESA com empresa no Simples). */
    simples: simplesConfigSchema.prefault({}),
    /** Padrao da tabela por tributo (uma fonte por tributo). IRPJ/CSLL/DAS de regime ficam nos parametros acima ou aqui, nunca nos dois. */
    tributos: z
      .array(
        z.object({
          tributo: tributoPerfil,
          aliquota: aliquota6,
          incidencia: incidenciaTributo.default('DENTRO'),
          base: despesaBase.default('PRECO_SEM_TRIBUTOS'),
          ativo: z.boolean().default(true),
        }),
      )
      .max(30)
      .default([]),
    /** Excecoes por UF de destino. Campo ausente = vale a tabela legal. */
    icmsUf: z
      .array(
        z.object({
          uf,
          interestadual: aliquota6.nullish(),
          internaDestino: aliquota6.nullish(),
          fcp: aliquota6.nullish(),
          reducaoBase: aliquota6.nullish(),
          /** Sessao 52: MVA ORIGINAL da ICMS-ST para esta UF de destino; vence a MVA do item. 0 <= MVA < 10. */
          mva: mva6.nullish(),
          observacao: z.string().trim().max(300).default(''),
        }),
      )
      .max(27)
      .default([]),
  })
  .superRefine((v, ctx) => {
    const vistos = new Set<string>();
    v.tributos.forEach((t, i) => {
      if (vistos.has(t.tributo)) ctx.addIssue({ code: 'custom', path: ['tributos', i, 'tributo'], message: `Tributo ${t.tributo} repetido: uma fonte por tributo` });
      vistos.add(t.tributo);
    });
    const ufs = new Set<string>();
    v.icmsUf.forEach((x, i) => {
      if (ufs.has(x.uf)) ctx.addIssue({ code: 'custom', path: ['icmsUf', i, 'uf'], message: `UF ${x.uf} repetida na grade` });
      ufs.add(x.uf);
    });
    const calculada = v.simples.modoEfetiva === 'CALCULADA';
    if (calculada && vistos.has('DAS')) {
      ctx.addIssue({ code: 'custom', path: ['tributos'], message: 'Com a efetiva do Simples calculada não use a linha DAS na tabela (ela venceria o cálculo)' });
    }
    if (calculada && v.regimeApuracao !== 'SIMPLES' && v.regimeApuracao !== 'SIMPLES_HIBRIDO' && v.regimeApuracao !== 'HERDAR_DA_EMPRESA') {
      ctx.addIssue({ code: 'custom', path: ['simples', 'modoEfetiva'], message: 'A efetiva calculada só vale nos regimes Simples e Simples híbrido' });
    }
    // CALCULADA: a efetiva sai dos Anexos; a digitada so e exigida no HIBRIDO como reserva (erro claro no calculo se faltar e o Anexo nao tiver partilha confirmada).
    if (v.regimeApuracao === 'SIMPLES' && !calculada && (v.dasEfetiva === undefined || v.dasEfetiva === null) && !vistos.has('DAS')) {
      ctx.addIssue({ code: 'custom', path: ['dasEfetiva'], message: 'Informe a alíquota efetiva do DAS' });
    }
    if (v.regimeApuracao === 'SIMPLES_HIBRIDO' && !calculada && (v.dasEfetivaSemCbsIbs === undefined || v.dasEfetivaSemCbsIbs === null)) {
      ctx.addIssue({ code: 'custom', path: ['dasEfetivaSemCbsIbs'], message: 'Informe a alíquota efetiva do DAS sem CBS/IBS' });
    }
  });
export type PerfilTributarioDto = z.infer<typeof perfilTributarioSchema>;

export const criarTabelaPrecoSchema = z.object({
  codigo: z.string().trim().min(1).max(40),
  nome: z.string().trim().min(1).max(200),
  isDefault: z.boolean().default(false),
  ativa: z.boolean().default(true),
  /** P3: o preco praticado digitado inclui CBS/IBS (tabela de venda a nao contribuintes). */
  precoIncluiCbsIbs: z.boolean().default(false),
});
export type CriarTabelaPrecoDto = z.infer<typeof criarTabelaPrecoSchema>;

export const atualizarTabelaPrecoSchema = z
  .object({
    nome: z.string().trim().min(1).max(200),
    isDefault: z.boolean(),
    ativa: z.boolean(),
    precoIncluiCbsIbs: z.boolean(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Informe ao menos um campo');
export type AtualizarTabelaPrecoDto = z.infer<typeof atualizarTabelaPrecoSchema>;

export const listarTabelasPrecoSchema = paginacao.extend({
  ativa: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
});
export type ListarTabelasPrecoQuery = z.infer<typeof listarTabelasPrecoSchema>;

/** Nova vigencia: fecha a vigente (validTo = validFrom) e insere esta. Valores nunca sao editados. */
export const novaVigenciaSchema = z.object({
  validFrom: isoDate,
  /** Fracao da TABELA; ausente/null = vale o padrao da empresa (DP6). */
  desiredProfitRate: taxa.nullish(),
  minimumProfitRate: taxa.nullish(),
  motivo: z.string().trim().min(1).max(300).optional(),
  despesas: z
    .array(
      z.object({
        description: z.string().trim().min(1).max(200),
        kind: despesaKind,
        rate: taxa,
        taxCode: despesaTaxCode.nullish(),
        base: despesaBase.default('PRECO_SEM_TRIBUTOS'),
      }),
    )
    .max(50)
    .default([]),
  /**
   * Sessao 51: ESTRUTURADO e o padrao (perfil tributario tipado; sem `perfilTributario` nasce com regime herdado da empresa e sem linhas).
   * LEGADO (tributo como despesa variavel, caminho dos goldens) so para vigencias que precisam do comportamento antigo (importador da FMC, testes).
   */
  modo: z.enum(['ESTRUTURADO', 'LEGADO']).default('ESTRUTURADO'),
  perfilTributario: perfilTributarioSchema.optional(),
}).superRefine((v, ctx) => {
  if (v.modo === 'LEGADO') {
    if (v.perfilTributario !== undefined) ctx.addIssue({ code: 'custom', path: ['perfilTributario'], message: 'Vigência legada não tem perfil tributário' });
    return;
  }
  v.despesas.forEach((d, i) => {
    if (d.taxCode !== undefined && d.taxCode !== null) {
      ctx.addIssue({ code: 'custom', path: ['despesas', i, 'taxCode'], message: 'No modo estruturado o tributo vai no perfil tributário; despesa com marca de tributo não é permitida' });
    }
    if (d.kind === 'TRIBUTO') {
      ctx.addIssue({ code: 'custom', path: ['despesas', i, 'kind'], message: 'No modo estruturado a despesa do tipo Tributo não é permitida: use o perfil tributário' });
    }
  });
});
export type NovaVigenciaDto = z.infer<typeof novaVigenciaSchema>;

export interface DespesaResponse {
  ordem: number;
  description: string;
  kind: z.infer<typeof despesaKind>;
  rate: string;
  taxCode: z.infer<typeof despesaTaxCode> | null;
  base: z.infer<typeof despesaBase>;
}

/** Perfil tributario gravado na vigencia ESTRUTURADA (decimais como string). So com custo.ver, como as despesas. */
export interface PerfilTributarioResponse {
  regimeApuracao: z.infer<typeof regimeApuracao>;
  metaLucro: z.infer<typeof metaLucro>;
  ufOrigem: string | null;
  aliqLucroReal: string;
  presuncaoIrpjBens: string;
  presuncaoIrpjServicos: string;
  presuncaoCsllBens: string;
  presuncaoCsllServicos: string;
  aliqIrpj: string;
  aliqCsll: string;
  adicionalIrpj: boolean;
  acrescimoLc224: boolean;
  dasEfetiva: string | null;
  dasEfetivaSemCbsIbs: string | null;
  icmsFixo: boolean;
  derivacaoUf: 'LIQUIDO_DENTRO' | 'MESMO_LUCRO';
  /** Sessao 52 (opcionais para quem monta a resposta sem eles; a API sempre envia). */
  difalBaseDupla?: boolean;
  simples?: SimplesConfigDto;
  tributos: Array<{ tributo: TributoPerfil; aliquota: string; incidencia: z.infer<typeof incidenciaTributo>; base: z.infer<typeof despesaBase>; ativo: boolean }>;
  icmsUf: Array<{ uf: string; interestadual: string | null; internaDestino: string | null; fcp: string | null; reducaoBase: string | null; mva?: string | null; observacao: string }>;
}

export interface VigenciaResponse {
  id: string;
  /** Sessao 50: LEGADO (tributo como despesa variavel) ou ESTRUTURADO (perfil tributario tipado). */
  modo?: z.infer<typeof modoTributario>;
  perfilTributario?: PerfilTributarioResponse;
  validFrom: string;
  validTo: string | null;
  /** Valor da tabela; null = segue o padrao da empresa. */
  desiredProfitRate: string | null;
  minimumProfitRate: string | null;
  /** Valor resolvido na data de hoje (tabela > empresa) e a origem (DP6). `lucroDesejado` nulo = nenhum dos dois define. */
  lucroDesejado: string | null;
  lucroDesejadoOrigem: OrigemLucro;
  lucroMinimo: string;
  lucroMinimoOrigem: OrigemLucro;
  /** Estrutura de despesa da tabela: so com custo.ver (a API omite para VENDEDOR e LEITURA). */
  despesas?: DespesaResponse[];
  /** Soma dos % das despesas da vigencia (fracao), calculada na API; so com custo.ver, como `despesas`. */
  totalDespesas?: string;
}

export interface TabelaPrecoResponse {
  id: string;
  codigo: string;
  nome: string;
  isDefault: boolean;
  ativa: boolean;
  /** P3: preco praticado inclui CBS/IBS. */
  precoIncluiCbsIbs: boolean;
  vigencias?: VigenciaResponse[];
  createdAt: string;
  updatedAt: string;
}

// ---- Sessao 50: tabela legal do ICMS e previa da conversao LEGADO -> ESTRUTURADO ----

/** Linha da tabela legal global do ICMS por UF (interna/fcp nulos = a confirmar). */
export interface IcmsUfLegalResponse {
  uf: string;
  interna: string | null;
  fcp: string | null;
  status: 'CONFIRMADA' | 'A_CONFIRMAR';
  fonte: string;
  obs: string;
  validFrom: string;
  validTo: string | null;
}

export const previaConversaoQuerySchema = z.object({
  /** Data de referencia (vigencia a converter e custos). Default: hoje. */
  data: isoDate.optional(),
  /** Condicao de pagamento do calculo antes x depois. Default: a padrao da tabela. */
  condicaoId: z.string().min(1).optional(),
  /** Quantos itens entram na amostra do efeito no preco (1 a 50; padrao 5). */
  amostra: z.coerce.number().int().min(1).max(50).default(5),
});
export type PreviaConversaoQuery = z.infer<typeof previaConversaoQuerySchema>;

export interface PreviaConversaoResponse {
  tabelaId: string;
  vigenciaId: string;
  data: string;
  condicaoId: string | null;
  /** Motivos que impedem a conversao ou o calculo do efeito (ex.: empresa sem regime tributario). */
  bloqueios: Array<{ codigo: string; mensagem: string }>;
  avisos: Array<{ codigo: string; mensagem: string }>;
  /** Perfil proposto (nada e gravado: a conversao cria NOVA vigencia quando o usuario confirma). Nulo se bloqueado. */
  perfilProposto: PerfilTributarioResponse | null;
  /** Despesas que continuam como despesa variavel (comissao etc.), sem marca de tributo. */
  despesasMantidas: DespesaResponse[];
  /** Linhas de tributo que a conversao nao soube classificar: o usuario decide. */
  pendentes: Array<{ descricao: string; taxa: string; motivo: string }>;
  /** Corpo pronto para `POST /tabelas-preco/:id/vigencias` (nova vigencia estruturada a partir desta); a tela so troca `validFrom`/`motivo`. Nulo se bloqueado. */
  corpo: { modo: 'ESTRUTURADO'; desiredProfitRate: string | null; minimumProfitRate: string | null; despesas: Array<{ description: string; kind: 'COMISSAO' | 'OUTRA'; rate: string; base: 'PRECO_SEM_TRIBUTOS' | 'TOTAL_NF' }>; perfilTributario: PerfilTributarioResponse } | null;
  /** Efeito no preco sugerido (com IPI, a prazo) numa amostra de itens: antes (LEGADO) x depois (ESTRUTURADO). */
  efeito: Array<{ itemId: string; codigo: string; antes: string | null; depois: string | null; variacao: string | null; erro?: string }>;
}

export const icmsLegalQuerySchema = z.object({ data: isoDate.optional() });
export type IcmsLegalQuery = z.infer<typeof icmsLegalQuerySchema>;

// ---- Sessao 51: administracao da tabela legal do ICMS (SUPERADMIN) ----
export const registrarIcmsLegalSchema = z.object({
  uf,
  /** Nulo = a confirmar (o motor nao usa e avisa). */
  interna: aliquota6.nullish(),
  /** Ausente = 0 (o FCP incide so sobre produtos especificos; excecao por UF/item). */
  fcp: aliquota6.default('0'),
  /** Fonte oficial (URL obrigatoria). */
  fonte: z.string().trim().url('Informe a URL da fonte oficial').max(500),
  obs: z.string().trim().max(500).default(''),
  /** Inicio da vigencia; tem que ser depois da ultima vigencia da UF (nunca edita linha historica). */
  validFrom: isoDate,
});
export type RegistrarIcmsLegalDto = z.infer<typeof registrarIcmsLegalSchema>;

// ---- Sessao 51: CBS, IBS e IS em SOMENTE LEITURA (sem aliquota propria na tabela) ----
export const tributosReformaQuerySchema = z.object({
  data: isoDate.optional(),
  /** UF de destino (IBS do destino a partir de 2029). */
  ufDestino: uf.optional(),
  /** Item: aplica a reducao de CBS/IBS e o IS do cadastro fiscal dele. */
  itemId: z.string().min(1).optional(),
});
export type TributosReformaQuery = z.infer<typeof tributosReformaQuerySchema>;

export interface TributosReformaResponse {
  data: string;
  regime: 'LEGACY' | 'DUAL_2026' | 'TRANSICAO' | 'NOVO_2033';
  etapa: string;
  /** 2026: informativo (nao soma no total). */
  informativo: boolean;
  linhas: Array<{
    tributo: 'CBS' | 'IBS_UF' | 'IBS_MUN' | 'IS';
    aliquota: string;
    incidencia: 'FORA';
    /** Ex.: "lei - 2026 teste", "reducao do item 60%". */
    fonte: string;
    estimada: boolean;
    informativo: boolean;
    reducaoItem: string | null;
  }>;
}

// ---- Sessao 51b: UFs praticadas da tabela ----
export interface UfsPraticadasResponse {
  /** As que valem hoje: as salvas, ou (sem salvas) a UF de origem + as UFs dos clientes com orcamento. */
  efetivas: string[];
  /** Sugestao automatica e o motivo. */
  sugeridas: Array<{ uf: string; motivo: 'ORIGEM' | 'CLIENTES' }>;
  /** Escolha salva (null = nunca editada: vale a sugestao). */
  salvas: string[] | null;
  ufOrigem: string | null;
}
export const ufsPraticadasSchema = z.object({ ufs: z.array(uf).max(27).refine((l) => new Set(l).size === l.length, 'UF repetida') });
export type UfsPraticadasDto = z.infer<typeof ufsPraticadasSchema>;
